import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-icons-kit';
import { inputClasses } from 'styles/tailwindClassnames';
import { SelectMenu } from '../schema-builder/SelectMenu';
import { Condition, OPS } from './types';
import { MappingChip, EmptySlot } from './SlotParts';
import { ic_close } from 'react-icons-kit/md/ic_close';
import { SLOT_ROW } from './styles';
import { DragPathRef, StartPick, useDragOver } from './dragAndDrop';

function ConditionRow({
  condition,
  index,
  onUpdate,
  onRemove,
  readOnly,
  dragPathRef,
  onStartPick,
  sourceArmed,
}: {
  condition: Condition;
  index: number;
  onUpdate: (index: number, condition: Condition) => void;
  onRemove: (index: number) => void;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onStartPick?: StartPick;
  sourceArmed?: boolean;
}) {
  const { isOver, handlers, resetOver } = useDragOver(readOnly);
  const { t } = useTranslation();
  const requiresValue = !['not_null', 'is_null'].includes(condition.op);
  const setField = (p: string[]) =>
    onUpdate(index, { ...condition, field: p.join('.') });
  const fieldPath = condition.field ? condition.field.split('.') : null;

  // Built from the same parts as a field row: the teal chip holds the path
  // with the divider and ×; the operator and its value sit on the line below.
  // The × removes the whole condition — one without a field is meaningless.
  const removeButton = !readOnly ? (
    <button
      type="button"
      onClick={() => onRemove(index)}
      aria-label={t('Remove condition')}
      className="flex shrink-0 items-center px-1 text-ui-text-primary-text hover:text-ui-cta-primary"
    >
      <Icon icon={ic_close} size={18} />
    </button>
  ) : undefined;

  return (
    <div data-slot-row tabIndex={-1} className={SLOT_ROW}>
      {fieldPath ? (
        <>
          <MappingChip
            path={fieldPath}
            showPreview={false}
            notInSample={false}
            readOnly={readOnly}
            onClear={() => onRemove(index)}
            clearLabel={t('Remove condition')}
          />
          <div className="mt-1.5 flex items-center gap-2">
            <SelectMenu
              value={condition.op}
              options={OPS.map((op) => ({
                value: op.v,
                label: t(op.labelKey as any),
              }))}
              onChange={(v) => onUpdate(index, { ...condition, op: v })}
              disabled={readOnly}
              className="w-40 shrink-0"
              compact
              ariaLabel={t('Operator')}
            />
            {requiresValue && (
              <input
                value={condition.value || ''}
                onChange={(e) =>
                  onUpdate(index, { ...condition, value: e.target.value })
                }
                placeholder={t('value')}
                disabled={readOnly}
                aria-label={t('value')}
                className={`${inputClasses.standardSm} min-w-0 flex-1 font-mono text-body-sm`}
              />
            )}
          </div>
        </>
      ) : (
        <EmptySlot
          label={t('Drop a source field')}
          isOver={isOver}
          sourceArmed={!!sourceArmed}
          readOnly={readOnly}
          handlers={handlers}
          onActivate={(el) => onStartPick?.(setField, el)}
          onDropPath={() => {
            resetOver();
            if (dragPathRef.current) setField(dragPathRef.current);
          }}
          extra={removeButton}
        />
      )}
    </div>
  );
}

export default ConditionRow;
